import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MOODS, moodById } from "@/lib/constants";
import MoodBadge from "@/components/MoodBadge";
import { Flame } from "lucide-react";
import { toast } from "sonner";

function last30() {
  const days = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

export default function MoodTracker({ user }) {
  const [logs, setLogs] = useState([]);
  const [streak, setStreak] = useState(0);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [l, s] = await Promise.all([
      api.get(`/mood-logs/${user.id}`, { params: { days: 30 } }),
      api.get(`/users/${user.id}/streak`),
    ]);
    setLogs(l.data);
    setStreak(s.data.streak);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const logMood = async (moodId) => {
    setSelected(moodId);
    await api.post("/mood-logs", { user_id: user.id, mood: moodId });
    toast.success("Mood logged for today.");
    await load();
  };

  const byDate = Object.fromEntries(logs.map((l) => [l.date, l.mood]));
  const days = last30();

  // counts
  const counts = MOODS.map((m) => ({ ...m, count: logs.filter((l) => l.mood === m.id).length }));
  const total = counts.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div
        className="rounded-2xl border p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
        data-testid="mood-header"
      >
        <div>
          <div className="text-xs font-medium tracking-widest uppercase mb-1" style={{ color: "var(--exhale-text-muted)" }}>
            Mood Tracker
          </div>
          <h2 className="font-display text-3xl font-extrabold" style={{ color: "var(--exhale-text)" }}>
            How&apos;s your inner weather?
          </h2>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border" style={{ borderColor: "var(--exhale-border)", background: "var(--exhale-bg)" }} data-testid="streak-display">
          <Flame className={`w-8 h-8 ${streak > 0 ? "flame-pulse" : ""}`} style={{ color: streak > 0 ? "#FF5A36" : "#6E6778" }} />
          <div>
            <div className="font-display text-2xl font-extrabold ember-text">{streak}</div>
            <div className="text-xs font-mono uppercase" style={{ color: "var(--exhale-text-muted)" }}>day streak</div>
          </div>
        </div>
      </div>

      <div
        className="rounded-2xl border p-6 sm:p-8"
        style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
      >
        <h3 className="font-display text-lg font-semibold mb-3" style={{ color: "var(--exhale-text)" }}>
          Log today&apos;s mood
        </h3>
        <div className="flex flex-wrap gap-2">
          {MOODS.map((m) => (
            <button
              key={m.id}
              onClick={() => logMood(m.id)}
              data-testid={`log-mood-${m.id}`}
              className="transition-transform hover:scale-105"
              style={{ opacity: selected === m.id ? 1 : 0.85 }}
            >
              <MoodBadge moodId={m.id} size="lg" />
            </button>
          ))}
        </div>
      </div>

      <div
        className="rounded-2xl border p-6 sm:p-8"
        style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
      >
        <h3 className="font-display text-lg font-semibold mb-4" style={{ color: "var(--exhale-text)" }}>
          Last 30 days
        </h3>
        <div className="grid grid-cols-10 sm:grid-cols-15 gap-1.5" data-testid="mood-heatmap">
          {days.map((d) => {
            const mid = byDate[d];
            const m = mid ? moodById(mid) : null;
            return (
              <div
                key={d}
                title={`${d}${m ? ` · ${m.label}` : ""}`}
                className="aspect-square rounded-md"
                style={{
                  background: m ? m.bg : "var(--exhale-badge)",
                  border: `1px solid ${m ? m.color + "60" : "var(--exhale-border)"}`,
                }}
                data-testid={`heatmap-cell-${d}`}
              />
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {counts.map((m) => {
            const pct = Math.round((m.count / total) * 100);
            return (
              <div key={m.id} className="flex items-center gap-3" data-testid={`mood-stat-${m.id}`}>
                <div className="w-28 shrink-0">
                  <MoodBadge moodId={m.id} />
                </div>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--exhale-badge)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: m.color }}
                  />
                </div>
                <div className="w-10 text-right font-mono text-xs" style={{ color: "var(--exhale-text-muted)" }}>
                  {m.count}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
